import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { GanttChart } from "@/components/Gantt/GanttChart";
import { TaskModal } from "@/components/Tasks/TaskModal";
import { PhaseModal } from "@/components/Projects/PhaseModal";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function GanttPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState("all");
  const [ganttMap, setGanttMap] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [taskModal, setTaskModal] = useState(null);
  const [phaseModal, setPhaseModal] = useState(null);
  const canEdit = user?.role === "admin" || user?.role === "pm";
  const canTimeline = (p) => user?.role === "admin" || (p.created_by || p.owner_id) === user?.id;

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const target = useMemo(
    () => (selected === "all" ? projects.slice(0, 8) : projects.filter((p) => p.id === selected)),
    [projects, selected]
  );

  const fetchGantt = async () => {
    if (target.length === 0) return;
    setLoading(true);
    const results = await Promise.all(target.map((p) => api.get(`/projects/${p.id}/gantt`)));
    const map = {};
    target.forEach((p, i) => { map[p.id] = results[i].data; });
    setGanttMap(map);
    setLoading(false);
  };

  useEffect(() => { if (projects.length) fetchGantt(); }, [projects, selected]);

  const toggleExpand = (projectId) => {
    setExpanded((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const { rows, milestones } = useMemo(() => {
    const allRows = [];
    const allMilestones = [];
    target.forEach((p) => {
      const g = ganttMap[p.id];
      if (!g) return;
      const isMulti = selected === "all";
      const isOpen = !isMulti || !!expanded[p.id];
      if (isMulti) {
        allRows.push({ id: `hdr-${p.id}`, type: "phase", label: p.name.toUpperCase(), start: null, end: null, isProjectHeader: true, projectId: p.id, expanded: isOpen });
      }
      if (!isOpen) return;
      const draggable = canTimeline(p);
      g.phases.forEach((ph) => {
        allRows.push({ id: ph.id, type: "phase", label: ph.name, department: ph.department, start: ph.start_date, end: ph.target_end_date, status: ph.status, completion_pct: ph.completion_pct, onHold: p.status === "On Hold" });
        ph.tasks.forEach((t) => allRows.push({ id: t.id, type: "task", label: t.title, department: t.department, start: t.start_date, end: t.due_date, status: t.status, dependencies: t.dependencies, draggable, completion_pct: t.completion_pct, actual_completion_date: t.actual_completion_date, assignee_names: t.assignee_names, onHold: p.status === "On Hold" }));
      });
      if (g.unassigned_tasks?.length > 0) {
        allRows.push({ id: `unassigned-${p.id}`, type: "phase", label: "Unassigned Tasks", start: null, end: null });
        g.unassigned_tasks.forEach((t) => allRows.push({ id: t.id, type: "task", label: t.title, department: t.department, start: t.start_date, end: t.due_date, status: t.status, dependencies: t.dependencies, draggable, completion_pct: t.completion_pct, actual_completion_date: t.actual_completion_date, assignee_names: t.assignee_names, onHold: p.status === "On Hold" }));
      }
      allMilestones.push(...g.milestones);
    });
    return { rows: allRows, milestones: allMilestones };
  }, [target, ganttMap, expanded, selected]);

  const handleReschedule = async (taskId, start, end) => {
    try {
      await api.patch(`/tasks/${taskId}/reschedule`, { start_date: start, due_date: end });
      toast.success("Task rescheduled");
      fetchGantt();
    } catch { toast.error("Failed to reschedule"); }
  };

  const openTaskById = async (taskId) => {
    const { data } = await api.get(`/tasks/${taskId}`);
    setTaskModal(data);
  };

  const isCreator = (p) => user?.role === "admin" || (p.created_by || p.owner_id) === user?.id;

  const openPhaseById = (phaseId) => {
    for (const [pid, g] of Object.entries(ganttMap)) {
      const ph = g.phases.find((p) => p.id === phaseId);
      if (ph) {
        const proj = projects.find((p) => p.id === pid);
        if (proj && isCreator(proj)) setPhaseModal({ phase: ph, canEditDates: true });
        return;
      }
    }
  };

  return (
    <div data-testid="gantt-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className="label-caps">Hero Feature</span>
          <h1 className="text-2xl font-display font-extrabold mt-1">Gantt Chart</h1>
        </div>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger data-testid="gantt-project-filter" className="w-64" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Active Projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <GanttChart rows={rows} milestones={milestones} onReschedule={handleReschedule} loading={loading} canDrag={canEdit}
        onHeaderClick={selected === "all" ? toggleExpand : undefined} onTaskClick={openTaskById} onPhaseClick={canEdit ? openPhaseById : undefined} />
      {taskModal && <TaskModal task={taskModal} onClose={() => setTaskModal(null)} onSaved={() => { setTaskModal(null); fetchGantt(); }} />}
      {phaseModal && <PhaseModal phase={phaseModal.phase} canEditDates={phaseModal.canEditDates} onClose={() => setPhaseModal(null)} onSaved={fetchGantt} onDeleted={fetchGantt} />}
    </div>
  );
}
